// Argus — WebcamWindow: single form A/V/A+V.
// Selectează webcam (sau "No webcam" pentru audio-only) + microfon + rezoluție (când camera reală) + calitate.
// Player: LiveScreenPlayer (V+A) sau LiveAudioPlayer (audio-only).
// Persist localStorage per agent.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Menu, MenuItem, ListItemIcon, ListItemText, Divider,
  Alert, Typography, CircularProgress, IconButton, Tooltip, Slider,
  Select, FormControl, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StopIcon from '@mui/icons-material/Stop';
import TuneIcon from '@mui/icons-material/Tune';
import CheckIcon from '@mui/icons-material/Check';
import { ArgusToolbar } from '../components/floating/ArgusToolbar';
import { fetchAgentCameras } from '../lib/cameras';
import { fetchAgentMicrophones } from '../lib/microphones';
import type { CameraDevice, MicrophoneDevice } from '../lib/enumDevices';
import {
  emitStreamStart, emitStreamStop, VIDEO_CONFIG_PRESETS,
  QUALITY_BITRATES, type StreamQuality,
} from '../lib/streams';
import { wsClient } from '../lib/wsClient';
import { browserRecorder } from '../lib/browserRecorder';
import { fetchAgents } from '../lib/api';
import { useQuery } from '@tanstack/react-query';
import { useAutoActionsStore } from '../store/autoActionsStore';
import DownloadIcon from '@mui/icons-material/Download';
import LiveScreenPlayer, { type LivePlayerHandle } from '../components/LiveScreenPlayer';
import { WebRtcStreamPlayer } from '../components/WebRtcStreamPlayer';
import LiveAudioPlayer from '../components/LiveAudioPlayer';

const NO_CAMERA = '__none__';

interface ResolutionOption {
  width: number;
  height: number;
  fps: number;
  label: string;
}

export interface WebcamWindowProps {
  agentId: string;
  agentOnline: boolean;
}

const STORAGE_KEY = (agentId: string) => `argus.webcam.${agentId}`;

interface WebcamPrefs {
  cameraId?: string;
  micId?: string;
  muted?: boolean;
  volume?: number; // bloc webrtc-audio-support 2026-05-11: 0..100; volume===0 implies muted.
  lastNonZeroVolume?: number; // pentru restore intuitiv la click unmute când volume e 0.
  quality?: StreamQuality;
  resolutionByCamera?: Record<string, string>;
  // Bloc webcam-modal-toolbar-redesign 2026-05-12 — CF-650..660.
  codec?: 'vp8' | 'h264'; // last user selection; fallback vp8 (smoother default).
  rtcEnabled?: boolean; // last user toggle; fallback true (RTC on default).
}

type CodecOption = 'vp8' | 'h264';

function loadPrefs(agentId: string): WebcamPrefs {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY(agentId)) ?? '{}'); }
  catch { return {}; }
}
function savePrefs(agentId: string, prefs: WebcamPrefs) {
  try { localStorage.setItem(STORAGE_KEY(agentId), JSON.stringify(prefs)); } catch {}
}

export function WebcamWindow({ agentId, agentOnline }: WebcamWindowProps): JSX.Element {
  const initialPrefs = useRef(loadPrefs(agentId));
  const [cameras, setCameras] = useState<CameraDevice[]>([]);
  const [microphones, setMicrophones] = useState<MicrophoneDevice[]>([]);
  const [selectedCameraId, setSelectedCameraId] = useState<string>(NO_CAMERA);
  const [selectedMicId, setSelectedMicId] = useState<string>('');
  const [selectedResolution, setSelectedResolution] = useState<string>('');
  // Bloc webrtc-audio-support 2026-05-11: dual state — explicitMute (click) + volume (0..100).
  // effectiveMuted = explicitMute || volume === 0. lastNonZeroVolume păstrează valoarea anterioară
  // > 0 pentru restore intuitiv la click unmute când slider e la 0.
  const [explicitMute, setExplicitMute] = useState<boolean>(initialPrefs.current.muted ?? false);
  const [volume, setVolume] = useState<number>(initialPrefs.current.volume ?? 100);
  const [lastNonZeroVolume, setLastNonZeroVolume] = useState<number>(
    initialPrefs.current.lastNonZeroVolume ?? (initialPrefs.current.volume && initialPrefs.current.volume > 0 ? initialPrefs.current.volume : 100)
  );
  const effectiveMuted = explicitMute || volume === 0;
  // Quality e hardcoded 'hd_plus' (max). Menu context removed — frontend trimite mereu max.
  // WebRTC ignoră complet bitrate (agent native format). MSE legacy fallback folosește hd_plus bitrate.
  const quality: StreamQuality = 'hd_plus';
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [enumError, setEnumError] = useState<string | null>(null);
  const [streamId, setStreamId] = useState<string | null>(null);
  const [streamStarting, setStreamStarting] = useState(false);
  const [opError, setOpError] = useState<string | null>(null);
  // Bloc 2026-05-10 webrtc-pivot: toggle pentru WebRTC mode (smooth concurrent cu altă app).
  // Bloc webcam-modal-toolbar-redesign 2026-05-12: persist în WebcamPrefs.rtcEnabled (CF-654).
  // Default `true` (RTC on) la lipsă; legacy localStorage key `argus.webcam.${agentId}.webrtc` migrat.
  const [useWebRtc, setUseWebRtc] = useState<boolean>(() => {
    const p = initialPrefs.current;
    if (typeof p.rtcEnabled === 'boolean') return p.rtcEnabled;
    // Legacy fallback: citește vechea cheie pentru migrare smooth la primul mount.
    try {
      const legacy = localStorage.getItem(`argus.webcam.${agentId}.webrtc`);
      if (legacy !== null) return legacy === '1';
    } catch { /* swallow */ }
    return true; // CF-654: default RTC on.
  });
  // Bloc webcam-modal-toolbar-redesign 2026-05-12: codec selection (CF-653).
  const [selectedCodec, setSelectedCodec] = useState<CodecOption>(() => {
    const p = initialPrefs.current;
    return p.codec === 'h264' ? 'h264' : 'vp8'; // CF-653: default vp8 (smoother).
  });
  // Active WebRTC mode = streaming via WebRtcStreamPlayer (NU emitStreamStart). Independent de streamId.
  const [webRtcActive, setWebRtcActive] = useState<boolean>(false);
  // Bloc recording-browser-pivot 2026-05-09: recording = browser-side via MediaRecorder.
  // recordingActive = local boolean (NU mai e commandId server-side). Source detect din isAudioOnly.
  const [recordingActive, setRecordingActive] = useState<boolean>(false);
  const playerRef = useRef<LivePlayerHandle>(null);
  const { data: agents } = useQuery({ queryKey: ['agents'], queryFn: fetchAgents, refetchInterval: 30_000 });
  const agentMeta = agents?.find((a) => a.id === agentId);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const isAudioOnly = selectedCameraId === NO_CAMERA;
  const selectedCamera = cameras.find((c) => c.id === selectedCameraId);
  const resolutionOptions: ResolutionOption[] = useMemo(() => {
    if (!selectedCamera) return [];
    const seen = new Set<string>();
    const opts: ResolutionOption[] = [];
    for (const m of selectedCamera.supportedModes ?? []) {
      const fps = Math.round(m.fps);
      const key = `${m.width}x${m.height}@${fps}`;
      if (!seen.has(key)) {
        seen.add(key);
        opts.push({ width: m.width, height: m.height, fps, label: `${m.width}×${m.height} @ ${fps}fps` });
      }
    }
    opts.sort((a, b) => (b.width * b.height) - (a.width * a.height));
    return opts;
  }, [selectedCamera]);

  // 2026-05-10: instant snap-picture la deschidere modal Webcam (UX request user).
  // Anterior: autoSnap doar din Quick Webcam sidebar (action='snap-picture'). Acum: TOATE
  // mount-urile declanșează snap automat după ce cameras loaded. Streaming = manual via Start.
  // Consume autoAction pentru cleanup map (no double-trigger).
  const autoSnapPendingRef = useRef<boolean>(true);
  const [snapJpegUrl, setSnapJpegUrl] = useState<string | null>(null);
  const [snapMeta, setSnapMeta] = useState<{ width: number; height: number; cameraName: string; capturedAt: string } | null>(null);
  const [snapBusy, setSnapBusy] = useState(false);
  const [snapError, setSnapError] = useState<string | null>(null);
  useEffect(() => {
    useAutoActionsStore.getState().consume(`${agentId}:webcam`);
  }, [agentId]);
  useEffect(() => () => {
    if (snapJpegUrl) URL.revokeObjectURL(snapJpegUrl);
  }, [snapJpegUrl]);

  // Enumerate on mount.
  useEffect(() => {
    if (!agentOnline) return;
    let cancelled = false;
    setLoadingDevices(true);
    setEnumError(null);
    Promise.all([fetchAgentCameras(agentId), fetchAgentMicrophones(agentId)])
      .then(([camRes, micRes]) => {
        if (cancelled) return;
        setCameras(camRes.cameras);
        setMicrophones(micRes.microphones);
        const prefs = initialPrefs.current;
        // Bloc webcam-modal-toolbar-redesign 2026-05-12 (CF-652): default cameră = ultima user
        // persistată dacă încă enumerată, altfel prima cameră reală (NU NO_CAMERA). Audio-only
        // (NO_CAMERA) rămâne selectabil explicit dar nu e default la primul mount.
        let camPref: string;
        if (prefs.cameraId && prefs.cameraId !== NO_CAMERA && camRes.cameras.find((c) => c.id === prefs.cameraId)) {
          camPref = prefs.cameraId;
        } else if (prefs.cameraId === NO_CAMERA) {
          camPref = NO_CAMERA; // user-ul a ales explicit audio-only ultima dată — respectă.
        } else if (camRes.cameras.length > 0) {
          camPref = camRes.cameras[0]!.id; // first real camera enumerated.
        } else {
          camPref = NO_CAMERA; // niciun cameră → audio-only.
        }
        setSelectedCameraId(camPref);
        const micPref = prefs.micId && micRes.microphones.find((m) => m.id === prefs.micId);
        const micDefault = micPref ?? micRes.microphones.find((m) => m.isDefault) ?? micRes.microphones[0];
        if (micDefault) setSelectedMicId(micDefault.id);
      })
      .catch((err: Error) => { if (!cancelled) setEnumError(err.message); })
      .finally(() => { if (!cancelled) setLoadingDevices(false); });
    return () => { cancelled = true; };
  }, [agentId, agentOnline]);

  // Quick Webcam: după ce cameras loaded + autoSnapPendingRef → pick first camera + max-res →
  // trigger snap (single-shot WS direct, NU livestream). NU schimbă selectedCameraId; doar
  // afișează poza într-un dialog separate.
  useEffect(() => {
    if (!autoSnapPendingRef.current) return;
    if (loadingDevices || cameras.length === 0) return;
    autoSnapPendingRef.current = false; // consume — nu repeta
    const cam = cameras[0];
    if (!cam) {
      setSnapError('Niciun cameră detectată pe agent.');
      return;
    }
    // Max-area resolution from supportedModes (sorted by area desc).
    const modes = (cam.supportedModes ?? []).slice().sort((a, b) => b.width * b.height - a.width * a.height);
    const top = modes[0];
    if (!top) {
      setSnapError(`Camera ${cam.friendlyName} nu raportează rezoluții suportate.`);
      return;
    }
    setSnapBusy(true);
    setSnapError(null);
    void wsClient.requestCameraPicture(agentId, cam.friendlyName, top.width, top.height, 'Quick webcam din sidebar')
      .then((res) => {
        // Decode base64 → Blob URL.
        const binary = atob(res.jpeg);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const blob = new Blob([bytes], { type: 'image/jpeg' });
        const url = URL.createObjectURL(blob);
        setSnapJpegUrl(url);
        setSnapMeta({ width: res.width, height: res.height, cameraName: res.cameraName, capturedAt: res.capturedAt });
      })
      .catch((err: Error) => setSnapError(err.message))
      .finally(() => setSnapBusy(false));
  }, [agentId, cameras, loadingDevices]);

  // Sync resolution when camera changes (or restore from prefs).
  useEffect(() => {
    if (isAudioOnly || resolutionOptions.length === 0) {
      setSelectedResolution('');
      return;
    }
    const stored = initialPrefs.current.resolutionByCamera?.[selectedCameraId];
    if (stored && resolutionOptions.some((r) => `${r.width}x${r.height}@${r.fps}` === stored)) {
      setSelectedResolution(stored);
    } else {
      const top = resolutionOptions[0]!;
      setSelectedResolution(`${top.width}x${top.height}@${top.fps}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCameraId, resolutionOptions.length]);

  // Persist preferences.
  useEffect(() => {
    if (!selectedMicId && !selectedCameraId) return;
    const prefs = loadPrefs(agentId);
    prefs.cameraId = selectedCameraId;
    if (selectedMicId) prefs.micId = selectedMicId;
    if (selectedResolution && !isAudioOnly) {
      prefs.resolutionByCamera = { ...(prefs.resolutionByCamera ?? {}), [selectedCameraId]: selectedResolution };
    }
    prefs.muted = explicitMute;
    prefs.volume = volume;
    prefs.lastNonZeroVolume = lastNonZeroVolume;
    // Bloc webcam-modal-toolbar-redesign 2026-05-12 — CF-658.
    prefs.codec = selectedCodec;
    prefs.rtcEnabled = useWebRtc;
    savePrefs(agentId, prefs);
  }, [agentId, selectedCameraId, selectedMicId, selectedResolution, explicitMute, volume, lastNonZeroVolume, quality, isAudioOnly, selectedCodec, useWebRtc]);

  const startStream = async () => {
    if (!agentOnline || streamStarting || streamId || webRtcActive) return;
    // Bloc 2026-05-10 webrtc-pivot: dacă useWebRtc + camera selectată (NU audio-only), pornim
    // path-ul WebRTC. Player-ul self-init signaling — nu mai chemăm emitStreamStart pentru
    // path-ul ăla. Existing flow ffmpeg pipe rămâne pentru audio-only + fallback.
    if (useWebRtc && !isAudioOnly && selectedCameraId) {
      setWebRtcActive(true);
      return;
    }
    setStreamStarting(true);
    setOpError(null);
    try {
      if (!selectedMicId) throw new Error('Select a microphone');
      const micName = microphones.find((m) => m.id === selectedMicId)?.friendlyName ?? selectedMicId;
      if (isAudioOnly) {
        const r = await emitStreamStart(agentId, '', VIDEO_CONFIG_PRESETS.medium!, {
          sources: ['audio'],
          audioId: micName,
        });
        setStreamId(r.streamId);
      } else {
        if (!selectedCameraId) throw new Error('Select a camera');
        if (!selectedResolution) throw new Error('Select a resolution');
        const match = /^(\d+)x(\d+)@(\d+)$/.exec(selectedResolution);
        if (!match) throw new Error('Invalid resolution');
        const [w, h, f] = [parseInt(match[1]!), parseInt(match[2]!), parseInt(match[3]!)];
        const config = { width: w, height: h, fps: f, bitrate: QUALITY_BITRATES[quality], label: `${w}x${h}@${f}fps` };
        const camName = cameras.find((c) => c.id === selectedCameraId)?.friendlyName ?? selectedCameraId;
        const r = await emitStreamStart(agentId, '', config, {
          sources: ['camera', 'audio'],
          cameraId: camName,
          audioId: micName,
          quality,
        });
        setStreamId(r.streamId);
      }
    } catch (err) {
      setOpError(err instanceof Error ? err.message : 'Failed to start stream');
    } finally {
      setStreamStarting(false);
    }
  };

  const stopStream = async () => {
    // WebRTC path: stop = unmount WebRtcStreamPlayer (close pe RTCPeerConnection + close call).
    if (webRtcActive) {
      setWebRtcActive(false);
      return;
    }
    if (!streamId) return;
    // Cascade: if recording active, stop it FIRST so download fires before stream tracks die.
    if (recordingActive) {
      const src = isAudioOnly ? 'audio' : 'webcam';
      browserRecorder.stopForAgent(agentId, src);
      setRecordingActive(false);
    }
    const id = streamId;
    setStreamId(null);
    try { await emitStreamStop(agentId, id); } catch { /* ignore */ }
  };

  // Restart stream cu setări noi (folosit la dropdown change în timpul stream).
  const restartStream = async (
    cameraId: string, micId: string, resolution: string, q: StreamQuality
  ) => {
    const oldId = streamId;
    setStreamId(null);
    if (oldId) {
      try { await emitStreamStop(agentId, oldId); } catch { /* ignore */ }
      await new Promise((r) => setTimeout(r, 300));
    }
    try {
      const micName = microphones.find((m) => m.id === micId)?.friendlyName ?? micId;
      if (cameraId === NO_CAMERA) {
        const r = await emitStreamStart(agentId, '', VIDEO_CONFIG_PRESETS.medium!, {
          sources: ['audio'],
          audioId: micName,
        });
        setStreamId(r.streamId);
      } else {
        const match = /^(\d+)x(\d+)@(\d+)$/.exec(resolution);
        if (!match) return;
        const [w, h, f] = [parseInt(match[1]!), parseInt(match[2]!), parseInt(match[3]!)];
        const config = { width: w, height: h, fps: f, bitrate: QUALITY_BITRATES[q], label: `${w}x${h}@${f}fps` };
        const camName = cameras.find((c) => c.id === cameraId)?.friendlyName ?? cameraId;
        const r = await emitStreamStart(agentId, '', config, {
          sources: ['camera', 'audio'],
          cameraId: camName,
          audioId: micName,
          quality: q,
        });
        setStreamId(r.streamId);
      }
    } catch (err) {
      setOpError(err instanceof Error ? err.message : 'Failed to restart stream');
    }
  };

  const pickResolutionFor = (cameraId: string): string => {
    if (cameraId === NO_CAMERA) return '';
    const cam = cameras.find((c) => c.id === cameraId);
    if (!cam) return '';
    const opts = (cam.supportedModes ?? []).map((m) => ({ width: m.width, height: m.height, fps: Math.round(m.fps) }));
    opts.sort((a, b) => (b.width * b.height) - (a.width * a.height));
    if (opts.length === 0) return '';
    const fresh = loadPrefs(agentId);
    const stored = fresh.resolutionByCamera?.[cameraId];
    if (stored && opts.some((o) => `${o.width}x${o.height}@${o.fps}` === stored)) return stored;
    const top = opts[0]!;
    return `${top.width}x${top.height}@${top.fps}`;
  };

  const handleCameraChange = (newCameraId: string) => {
    setSelectedCameraId(newCameraId);
    const newRes = pickResolutionFor(newCameraId);
    setSelectedResolution(newRes);
    if (streamId && selectedMicId) {
      void restartStream(newCameraId, selectedMicId, newRes, quality);
    }
  };

  const handleMicChange = (newMicId: string) => {
    setSelectedMicId(newMicId);
    if (streamId) void restartStream(selectedCameraId, newMicId, selectedResolution, quality);
  };

  const handleResolutionChange = (newRes: string) => {
    setSelectedResolution(newRes);
    if (streamId && !isAudioOnly && selectedMicId) {
      void restartStream(selectedCameraId, selectedMicId, newRes, quality);
    }
  };

  const startRecording = () => {
    if (!streamId || recordingActive) return;
    setOpError(null);
    try {
      const mediaStream = playerRef.current?.captureStream();
      if (!mediaStream) {
        throw new Error('Stream player not ready (no MediaStream available).');
      }
      const source = isAudioOnly ? 'audio' : 'webcam';
      browserRecorder.start({
        agentId,
        pcName: agentMeta?.pcName ?? agentMeta?.hostname ?? 'unknown',
        windowsUsername: agentMeta?.windowsUsername ?? agentMeta?.lastSeenUser ?? 'system',
        source,
        mediaStream,
        bitrate: source === 'audio' ? 128_000 : QUALITY_BITRATES[quality],
      });
      setRecordingActive(true);
    } catch (err) {
      setOpError(err instanceof Error ? err.message : 'Failed to start recording');
    }
  };

  const stopRecording = () => {
    if (!recordingActive) return;
    const source = isAudioOnly ? 'audio' : 'webcam';
    browserRecorder.stopForAgent(agentId, source);
    setRecordingActive(false);
  };

  // Cleanup pe unmount: stop stream + stop recording (download triggered).
  const streamIdRef = useRef<string | null>(null);
  useEffect(() => { streamIdRef.current = streamId; }, [streamId]);
  useEffect(() => {
    return () => {
      const sid = streamIdRef.current;
      if (sid) emitStreamStop(agentId, sid).catch(() => {});
      // browserRecorder.stop() declanșează download; sigur de invocat la unmount dacă active.
      browserRecorder.stopForAgent(agentId, 'webcam');
      browserRecorder.stopForAgent(agentId, 'audio');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // beforeunload guard: dacă stream/record activ, browser cere confirmare la close/refresh.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (streamId || recordingActive) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [streamId, recordingActive]);

  return (
    <Box data-role="webcam-panel" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <ArgusToolbar>
        {loadingDevices ? (
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} /><Typography variant="caption">Se enumeră dispozitivele…</Typography>
          </Box>
        ) : null}

        {/* Bloc webcam-modal-toolbar-redesign 2026-05-12 — CF-651: camera selector dropdown în toolbar. */}
        {!loadingDevices && cameras.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              value={selectedCameraId || NO_CAMERA}
              onChange={(e) => handleCameraChange(e.target.value)}
              disabled={!agentOnline}
              displayEmpty
              sx={{ fontSize: 13, height: 32 }}
            >
              <MenuItem value={NO_CAMERA}><em>Fără cameră (doar audio)</em></MenuItem>
              {cameras.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.friendlyName}{c.isDefault ? ' ★' : ''}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {/* Bloc webcam-modal-toolbar-redesign 2026-05-12 — CF-650/655: ButtonGroup codec + RTC.
             Codec ToggleButtonGroup (exclusive) + Divider vertical + RTC ToggleButton.
             Disabled în timpul stream activ (schimbare codec/RTC = stop+restart manual). */}
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }}>
          <ToggleButtonGroup
            value={selectedCodec}
            exclusive
            size="small"
            onChange={(_, v) => { if (v !== null) setSelectedCodec(v as CodecOption); }}
            disabled={streamId !== null || webRtcActive}
            sx={{ height: 32 }}
          >
            <Tooltip title="VP8 (smooth, recomandat — universal compat Win10+Win11)">
              <ToggleButton value="vp8" sx={{ fontSize: 10, fontWeight: 600, px: 1, py: 0.5 }}>VP8</ToggleButton>
            </Tooltip>
            <Tooltip title="H.264 experimental — known broken pe Win10 (backlog webrtc-h264-win10-investigation). Calitate +4Mbps pe Win11.">
              <ToggleButton value="h264" sx={{ fontSize: 10, fontWeight: 600, px: 1, py: 0.5 }}>H.264</ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
          <Tooltip title={useWebRtc ? 'WebRTC mode ON (smooth peer connection). Click pentru OFF (MSE legacy).' : 'WebRTC mode OFF (legacy MSE pipe). Click pentru ON.'}>
            <span>
              <ToggleButton
                value="rtc"
                selected={useWebRtc}
                size="small"
                onChange={() => setUseWebRtc((v) => !v)}
                disabled={streamId !== null || webRtcActive || isAudioOnly}
                sx={{ fontSize: 10, fontWeight: 600, px: 1, py: 0.5, height: 32 }}
              >
                RTC
              </ToggleButton>
            </span>
          </Tooltip>
        </Box>

        {(streamId || webRtcActive) ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={effectiveMuted ? 'Unmute' : 'Mute'}>
              <IconButton
                size="small"
                onClick={() => {
                  // Click mute btn: dacă muted prin volume===0, restore la lastNonZeroVolume + clear explicit.
                  // Altfel toggle explicitMute (volume rămâne neschimbat — slider continuă să arate setarea curentă).
                  if (effectiveMuted && volume === 0) {
                    setVolume(lastNonZeroVolume > 0 ? lastNonZeroVolume : 100);
                    setExplicitMute(false);
                  } else {
                    setExplicitMute((m) => !m);
                  }
                }}
              >
                {effectiveMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
              </IconButton>
            </Tooltip>
            <Slider
              size="small"
              value={effectiveMuted ? 0 : volume}
              min={0}
              max={100}
              onChange={(_, val) => {
                const v = Array.isArray(val) ? (val[0] ?? 0) : val;
                setVolume(v);
                if (v > 0) {
                  setExplicitMute(false);
                  setLastNonZeroVolume(v);
                }
              }}
              sx={{ width: 80, mx: 0.5 }}
              aria-label="Volume"
            />
          </Box>
        ) : null}

        <Tooltip title={(streamId || webRtcActive) ? 'Stop livestream' : (useWebRtc ? 'Start livestream (WebRTC smooth mode)' : 'Start livestream')}>
          <span>
            <IconButton
              size="small"
              color={(streamId || webRtcActive) ? 'error' : 'primary'}
              disabled={!agentOnline || streamStarting || loadingDevices}
              onClick={() => void ((streamId || webRtcActive) ? stopStream() : startStream())}
            >
              {streamStarting ? <CircularProgress size={18} /> : (streamId || webRtcActive) ? <VideocamOffIcon /> : <VideocamIcon />}
            </IconButton>
          </span>
        </Tooltip>

        {/* Bloc webcam-modal-toolbar-redesign 2026-05-12: IconButton RTC vechi mutat în ToggleButtonGroup. */}

        <Tooltip title={recordingActive ? 'Oprește înregistrarea (.webm download)' : (streamId ? 'Pornește înregistrarea (browser-side .webm)' : 'Pornește streaming-ul pentru a activa înregistrarea')}>
          <span>
            <IconButton
              size="small"
              color="error"
              disabled={!agentOnline || !streamId}
              onClick={() => (recordingActive ? stopRecording() : startRecording())}
            >
              {recordingActive ? <StopIcon /> : <FiberManualRecordIcon />}
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Settings">
          <span>
            <IconButton size="small" disabled={loadingDevices} onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <TuneIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {/* Bloc webcam-modal-toolbar-redesign 2026-05-12 — CF-651: secțiune CAMERA mutată în toolbar dropdown. */}

          {!isAudioOnly && resolutionOptions.length > 0 && (
            <MenuItem disabled dense sx={{ opacity: '0.7 !important' }}>
              <Typography variant="caption" color="text.secondary">RESOLUTION</Typography>
            </MenuItem>
          )}
          {!isAudioOnly && resolutionOptions.map((r) => {
            const key = `${r.width}x${r.height}@${r.fps}`;
            return (
              <MenuItem
                key={key}
                dense
                onClick={() => { handleResolutionChange(key); setMenuAnchor(null); }}
              >
                <ListItemIcon>{selectedResolution === key ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
                <ListItemText primary={r.label} />
              </MenuItem>
            );
          })}

          <Divider />
          <MenuItem disabled dense sx={{ opacity: '0.7 !important' }}>
            <Typography variant="caption" color="text.secondary">MICROPHONE</Typography>
          </MenuItem>
          {microphones.map((m) => (
            <MenuItem
              key={m.id}
              dense
              onClick={() => { handleMicChange(m.id); setMenuAnchor(null); }}
            >
              <ListItemIcon>{selectedMicId === m.id ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
              <ListItemText primary={m.friendlyName + (m.isDefault ? ' ★' : '')} />
            </MenuItem>
          ))}
        </Menu>

      </ArgusToolbar>

      {!agentOnline ? <Alert severity="warning" variant="filled" sx={{ borderRadius: 0, py: 0.5 }}>Agent offline</Alert> : null}
      {enumError ? <Alert severity="error" variant="outlined" sx={{ borderRadius: 0, py: 0.5 }} onClose={() => setEnumError(null)}>Enumeration: {enumError}</Alert> : null}
      {opError ? <Alert severity="error" variant="outlined" sx={{ borderRadius: 0, py: 0.5 }} onClose={() => setOpError(null)}>{opError}</Alert> : null}

      <Box data-role="webcam-content" sx={{ flexGrow: 1, overflow: 'auto', p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'action.hover', position: 'relative' }}>
        {webRtcActive && selectedCamera ? (
          <WebRtcStreamPlayer
            agentId={agentId}
            cameraId={selectedCamera.friendlyName}
            {...(selectedMicId ? { microphoneId: microphones.find((m) => m.id === selectedMicId)?.friendlyName ?? selectedMicId } : {})}
            stunUrl="stun:165.232.81.235:3478"
            muted={effectiveMuted}
            volume={volume / 100}
            flipHorizontal
            codec={selectedCodec}
            onTerminated={(reason) => { setWebRtcActive(false); setOpError(`WebRTC: ${reason}`); }}
          />
        ) : streamId ? (
          isAudioOnly ? (
            <LiveAudioPlayer ref={playerRef} streamId={streamId} muted={effectiveMuted} volume={volume / 100} onTerminated={(reason: string) => { setStreamId(null); setOpError(`Stream terminated: ${reason}`); }} />
          ) : (
            <LiveScreenPlayer ref={playerRef} streamId={streamId} hasAudio muted={effectiveMuted} volume={volume / 100} flipHorizontal onTerminated={(reason) => { setStreamId(null); setOpError(`Stream terminated: ${reason}`); }} />
          )
        ) : snapBusy ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">Se captează imagine de la cameră...</Typography>
          </Box>
        ) : snapError ? (
          <Alert severity="error" sx={{ maxWidth: 480 }} onClose={() => setSnapError(null)}>{snapError}</Alert>
        ) : snapJpegUrl ? (
          <>
            <Box component="img" src={snapJpegUrl} alt="Webcam snapshot" sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 1, transform: 'scaleX(-1)' }} />
            <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
              <IconButton
                size="small"
                onClick={() => {
                  const a = document.createElement('a');
                  a.href = snapJpegUrl;
                  a.download = `webcam-${snapMeta?.cameraName ?? 'capture'}-${Date.now()}.jpg`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                }}
                sx={{ bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' } }}
                title="Save snapshot ca .jpg"
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
              {snapMeta && (
                <Typography variant="caption" sx={{ bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', px: 1, py: 0.25, borderRadius: 0.5 }}>
                  {snapMeta.width}×{snapMeta.height} · {new Date(snapMeta.capturedAt).toLocaleTimeString('ro-RO')}
                </Typography>
              )}
            </Box>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {recordingActive ? '● REC (browser-side, .webm la stop)' : 'Apasă Livestream pentru a vedea + a putea înregistra.'}
          </Typography>
        )}
      </Box>

      {/* Status bar — afișează setările curente ale stream-ului. */}
      <Box data-role="webcam-statusbar" sx={{ borderTop: 1, borderColor: 'divider', backgroundColor: 'background.paper', px: 1, py: 0.5, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', minHeight: 24 }}>
        <Typography variant="caption" color="text.secondary"><strong>Camera:</strong> {selectedCamera?.friendlyName ?? (isAudioOnly ? '— (audio only)' : '—')}</Typography>
        {!isAudioOnly && <Typography variant="caption" color="text.secondary"><strong>Resolution:</strong> {selectedResolution || '—'}</Typography>}
        <Typography variant="caption" color="text.secondary"><strong>Microphone:</strong> {microphones.find((m) => m.id === selectedMicId)?.friendlyName ?? '—'}</Typography>
      </Box>

    </Box>
  );
}
